// src/components/ui/Charts/AreaChart.tsx
import React, { useMemo, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart as RechartsAreaChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { COLORS } from '@/theme/colors';
import { CustomTooltip } from './CustomTooltip';
import { ChartRenderer } from './ChartRenderer';
import { DATE_FORMATS, LOCALES } from '@/utils/constants';
import { formatDateTime, utcToLocal, formatSmartDate } from '@/utils/dateUtils';

export interface AreaDataSeries {
  name: string;
  dataKey: string;
  color?: string;
  fillOpacity?: number;
  stackId?: string;
  hidden?: boolean;
}

export interface AreaChartProps {
  // Основные данные и настройки
  data: Array<Record<string, any>>;
  series: AreaDataSeries[];
  xAxisKey?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  
  // Базовые настройки отображения (обратная совместимость)
  showGrid?: boolean;
  showLegend?: boolean;
  height?: number;
  className?: string;
  
  // Настройки данных
  valueType?: 'number' | 'percent' | 'currency' | 'compact';
  precision?: number;
  tooltipFormatter?: (value: any, name: string, props: any) => React.ReactNode;
  
  // Настройки форматирования дат
  xAxisDateFormat?: boolean;
  xAxisDateFormatType?: 'short' | 'full' | 'smart';
  dateFromUTC?: boolean;
  locale?: string;
  
  // Новые параметры для унифицированного управления
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'auto';
  minHeight?: number;
  aspectRatio?: number;
  isLoading?: boolean;
  error?: string | null;
  noData?: boolean;
  title?: string;
  subtitle?: string;
  hideFutureData?: boolean;
  
  // Колбэки и обработчики
  onSeriesToggle?: (dataKey: string, isActive: boolean) => void;
  onClick?: (data: any, index: number) => void;
}

/**
 * Компонент для отображения графиков с областями (AreaChart)
 * Поддерживает унифицированное управление размерами и состояниями через ChartRenderer
 */
export const AreaChart = forwardRef<HTMLDivElement, AreaChartProps>(({
  // Основные данные и настройки
  data,
  series,
  xAxisKey = 'name',
  xAxisLabel,
  yAxisLabel,
  
  // Базовые настройки отображения (обратная совместимость)
  showGrid = true,
  showLegend = true,
  height,
  className = '',
  
  // Настройки данных
  valueType = 'number',
  precision = 2,
  tooltipFormatter,
  
  // Настройки форматирования дат
  xAxisDateFormat = false,
  xAxisDateFormatType = 'short',
  dateFromUTC = true,
  locale = LOCALES.DEFAULT,
  
  // Новые параметры для унифицированного управления
  size = 'md',
  minHeight,
  aspectRatio,
  isLoading = false,
  error = null,
  noData = false,
  title,
  subtitle,
  // Колбэки и обработчики
  onSeriesToggle,
  onClick
}, ref) => {
  const { i18n } = useTranslation(['analytics', 'ui', 'common']);
  const currentLocale = i18n.language || locale;
  
  // Обратная совместимость для размеров
  const effectiveMinHeight = useMemo(() => {
    if (minHeight) return minHeight;
    if (height) return height;
    return undefined;
  }, [minHeight, height]);
  
  // Создаем палитру цветов на основе темы
  const defaultColors = [
    COLORS.primary,
    '#32a852',
    '#3273a8',
    '#a83232',
    '#a87832',
    '#8432a8',
  ];
  
  // Настройки отступов
  const margin = { 
    top: 5, 
    right: 30, 
    bottom: 5, 
    left: 20
  };
  
  // Функция форматирования меток оси X для дат
  const formatXAxisTick = useMemo(() => {
    if (!xAxisDateFormat) return undefined;
    
    return (value: string): string => {
      if (!value) return value;
      
      try {
        if (dateFromUTC) {
          switch (xAxisDateFormatType) {
            case 'smart':
              return formatSmartDate(value, false, currentLocale);
            case 'full':
              return formatDateTime(value, false, currentLocale);
            case 'short':
            default:
              // Форматируем только день и месяц
              const localDateTime = utcToLocal(value, currentLocale);
              if (!localDateTime) return value;
              return localDateTime.toFormat(DATE_FORMATS.SHORT_DATE);
          }
        } else {
          // Для дат не в UTC формате применяем стандартное форматирование
          return formatDateTime(value, false, currentLocale);
        }
      } catch (error) {
        console.error('Ошибка форматирования метки оси X:', error);
        return value;
      }
    };
  }, [xAxisDateFormat, xAxisDateFormatType, dateFromUTC, currentLocale]);
  
  // Функция форматирования подсказок с датами
  const formatTooltipLabel = useMemo(() => {
    if (!xAxisDateFormat) return undefined;
    
    return (label: string): string => {
      if (!label) return label;
      
      try {
        if (dateFromUTC) {
          // Используем умное форматирование для подсказок
          return formatSmartDate(label, true, currentLocale);
        } else {
          // Для дат не в UTC формате используем полное форматирование с временем
          return formatDateTime(label, true, currentLocale);
        }
      } catch (error) {
        console.error('Ошибка форматирования метки подсказки:', error);
        return label;
      }
    };
  }, [xAxisDateFormat, dateFromUTC, currentLocale]);
  
  // Обработка видимых/скрытых серий
  const visibleSeries = useMemo(() => 
    series.filter(serie => serie.hidden !== true),
  [series]);
  
  // Определяем, нет ли данных
  const hasNoData = useMemo(() => {
    return noData || !data || data.length === 0 || visibleSeries.length === 0;
  }, [noData, data, visibleSeries]);
  
  // Обработка клика на легенду
  const handleLegendClick = useMemo(() => {
    if (!onSeriesToggle) return undefined;
    return (entry: any) => {
      onSeriesToggle(entry.dataKey, !entry.inactive);
    };
  }, [onSeriesToggle]);
  
  return (
    <ChartRenderer
      size={size}
      minHeight={effectiveMinHeight}
      aspectRatio={aspectRatio}
      className={className}
      isLoading={isLoading}
      error={error}
      noData={hasNoData}
      title={title}
      subtitle={subtitle}
      ref={ref}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsAreaChart
          data={data}
          margin={margin}
          onClick={onClick}
        >
          {/* Сетка */}
          {showGrid && (
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={COLORS.secondary}
            />
          )}
          
          {/* Ось X */}
          <XAxis 
            dataKey={xAxisKey} 
            tick={{ fill: COLORS.text }} 
            tickFormatter={formatXAxisTick}
            label={xAxisLabel ? { 
              value: xAxisLabel, 
              position: 'insideBottom', 
              offset: -5 
            } : undefined}
          />
          
          {/* Ось Y */}
          <YAxis 
            tick={{ fill: COLORS.text }} 
            label={yAxisLabel ? { 
              value: yAxisLabel, 
              angle: -90, 
              position: 'insideLeft' 
            } : undefined}
          />
          
          {/* Тултип */}
          <Tooltip
            content={
              <CustomTooltip 
                valueType={valueType} 
                precision={precision} 
                formatter={tooltipFormatter}
                labelFormatter={formatTooltipLabel}
              />
            }
          />
          
          {/* Легенда */}
          {showLegend && (
            <Legend 
              onClick={handleLegendClick}
            />
          )}
          
          {/* Области графика для каждой серии */}
          {visibleSeries.map((serie, index) => {
            const color = serie.color || defaultColors[index % defaultColors.length];
            return (
              <Area
                key={serie.dataKey}
                type="monotone"
                dataKey={serie.dataKey}
                name={serie.name}
                stroke={color}
                fill={color}
                fillOpacity={serie.fillOpacity || 0.3}
                stackId={serie.stackId}
              />
            );
          })}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </ChartRenderer>
  );
});

AreaChart.displayName = 'AreaChart';

export default AreaChart;